/* Native details and anchor links work without JavaScript. Enhance on first open. */
(function () {
  function parchmentMotion(details) {
    if (details.dataset.reportParchment !== 'true') return () => {};
    const parchment = details.querySelector('[data-report-parchment-object]');
    if (!parchment) return () => {};
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const settle = () => details.classList.remove('is-unfolding');
    parchment.addEventListener('animationend', (event) => {
      if (event.target === parchment && event.animationName === 'report-parchment-settle') settle();
    });
    parchment.addEventListener('animationcancel', (event) => {
      if (event.target === parchment && event.animationName === 'report-parchment-settle') settle();
    });
    if (motion.addEventListener) motion.addEventListener('change', () => { if (motion.matches) settle(); });
    return () => {
      settle();
      // Native details opens immediately. This only animates a decorative object;
      // reading, tab navigation and the independent purchase form never wait.
      if (details.open && !motion.matches) details.classList.add('is-unfolding');
    };
  }
  function initReport(details) {
    if (details.dataset.reportReady) return;
    details.dataset.reportReady = 'true';
    const index = details.querySelector('[data-report-index]');
    const tabs = [...details.querySelectorAll('[data-report-tab]')];
    const pages = [...details.querySelectorAll('[data-report-page]')];
    const navigation = details.querySelector('[data-report-navigation]');
    const status = details.querySelector('[data-report-status]');
    const previous = details.querySelector('[data-report-prev]');
    const next = details.querySelector('[data-report-next]');
    const contents = details.querySelector('[data-report-contents]');
    if (!tabs.length || tabs.length !== pages.length || !index || !navigation || !previous || !next) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let selected = 0;
    index.setAttribute('role', 'tablist');
    tabs.forEach((tab, number) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', pages[number].id);
      pages[number].setAttribute('role', 'tabpanel');
      pages[number].tabIndex = 0;
    });
    const show = (number, { focusTab = false, animate = true } = {}) => {
      selected = Math.max(0, Math.min(number, pages.length - 1));
      tabs.forEach((tab, tabIndex) => {
        tab.setAttribute('aria-selected', String(tabIndex === selected));
        tab.tabIndex = tabIndex === selected ? 0 : -1;
        pages[tabIndex].hidden = tabIndex !== selected;
        pages[tabIndex].classList.remove('is-turning');
      });
      previous.disabled = selected === 0;
      next.disabled = selected === pages.length - 1;
      if (status) status.textContent = `${tabs[selected].textContent.trim().replace(/^\d+\s*/, '')} · ${selected + 1} of ${pages.length}`;
      if (focusTab) tabs[selected].focus({ preventScroll: true });
      if (animate && !reducedMotion.matches && details.open) pages[selected].classList.add('is-turning');
    };
    tabs.forEach((tab, number) => {
      const foldContents = () => {
        if (!contents) return;
        contents.open = false;
        pages[number].focus({ preventScroll: true });
      };
      tab.addEventListener('click', (event) => { event.preventDefault(); show(number); foldContents(); });
      tab.addEventListener('keydown', (event) => {
        let destination;
        if (event.key === 'ArrowRight') destination = (number + 1) % tabs.length;
        if (event.key === 'ArrowLeft') destination = (number - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') destination = 0;
        if (event.key === 'End') destination = tabs.length - 1;
        if (event.key === ' ' || event.key === 'Enter') destination = number;
        if (destination === undefined) return;
        event.preventDefault();
        show(destination, { focusTab: true });
        if (event.key === ' ' || event.key === 'Enter') foldContents();
      });
    });
    previous.addEventListener('click', () => { show(selected - 1); pages[selected].focus({ preventScroll: true }); });
    next.addEventListener('click', () => { show(selected + 1); pages[selected].focus({ preventScroll: true }); });
    details.classList.add('is-enhanced');
    navigation.hidden = false;
    show(0, { animate: false });
  }
  function modalReader(details) {
    // Native dialog is progressive enhancement: unsupported/disabled JS retains
    // the original details, page anchors and direct full-size PDF links.
    if (typeof document.createElement !== 'function') return null;
    const booklet = details.querySelector('.report-preview__booklet');
    const header = details.querySelector('[data-report-reader-header]');
    const closeButton = details.querySelector('[data-report-close]');
    const summary = details.querySelector('.report-preview__envelope');
    if (!booklet || !header || !closeButton || !summary) return null;
    const dialog = document.createElement('dialog');
    if (typeof dialog.showModal !== 'function') return null;
    dialog.className = 'report-preview__dialog';
    dialog.id = `${details.id}-dialog`;
    summary.setAttribute('aria-haspopup', 'dialog');
    summary.setAttribute('aria-controls', dialog.id);
    const title = header.querySelector('h3');
    if (title) dialog.setAttribute('aria-labelledby', title.id);
    else dialog.setAttribute('aria-label', 'Personal Ritual Report preview');
    details.appendChild(dialog);
    dialog.appendChild(booklet);
    header.hidden = false;
    details.classList.add('is-dialog-reader');
    const unlock = () => {
      if (!document.querySelector('.report-preview__dialog[open]')) document.documentElement.classList.remove('report-preview-modal-open');
    };
    const close = () => { if (dialog.open) dialog.close(); };
    closeButton.addEventListener('click', close);
    dialog.addEventListener('close', () => {
      // Ignore a queued close event if a rapid new interaction reopened it.
      if (dialog.open) return;
      details.open = false;
      const contents = details.querySelector('[data-report-contents]');
      if (contents) contents.open = false;
      unlock();
      summary.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
    });
    return {
      open() {
        if (!dialog.open && dialog.isConnected) {
          dialog.showModal();
          document.documentElement.classList.add('report-preview-modal-open');
          closeButton.focus({ preventScroll: true });
        }
      },
      close,
    };
  }
  const bind = (root) => root.querySelectorAll('[data-report-preview]').forEach((details) => {
    if (details.dataset.reportBound) return;
    details.dataset.reportBound = 'true';
    const unfold = parchmentMotion(details);
    const reader = modalReader(details);
    details.addEventListener('toggle', () => {
      unfold();
      if (details.open) { initReport(details); if (reader) reader.open(); }
      else if (reader) reader.close();
    });
    if (details.open) { initReport(details); unfold(); if (reader) reader.open(); }
  });
  bind(document);
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
